// src/utils/userStatusUtils.ts
//
// Central place for all user-status/role → CSS class / i18n-key mappings.
// Both ProfilePage and AdminUsers should derive their badge colours from here.

import { UserStatusEnum } from "../models/enums/UserStatusEnum";
import { UserRoleEnum } from "../models/enums/UserRoleEnum";
import { TFunction } from "i18next";
import { parseServerDate, formatDateTime } from "./dateUtils";

// ── Role CSS class map ────────────────────────────────────────────────────────
export const USER_ROLE_CSS: Record<string, string> = {
  [UserRoleEnum.ADMIN]:     "status-admin",
  [UserRoleEnum.MODERATOR]: "status-moderator",
  [UserRoleEnum.AUTHOR]:    "status-author",
  [UserRoleEnum.ROOKIE]:    "status-rookie",
};

// ── Status CSS class map ──────────────────────────────────────────────────────
export const USER_STATUS_CSS: Record<string, string> = {
  [UserStatusEnum.ACTIVE]:           "status-author",
  [UserStatusEnum.SUSPENDED]:        "status-suspended",
  [UserStatusEnum.BANNED]:           "status-banned",
  [UserStatusEnum.FROZEN_BY_USER]:   "status-frozen-by-user",
  [UserStatusEnum.DELETED_BY_USER]:  "status-deleted-by-user",
  [UserStatusEnum.DELETED_BY_ADMIN]: "status-deleted-by-admin",
  [UserStatusEnum.INACTIVE]:         "status-inactive",
};

// ── Role i18n key map ─────────────────────────────────────────────────────────
export const USER_ROLE_I18N_KEY: Record<string, string> = {
  [UserRoleEnum.ADMIN]:     "status_admin",
  [UserRoleEnum.MODERATOR]: "status_moderator",
  [UserRoleEnum.AUTHOR]:    "status_author",
  [UserRoleEnum.ROOKIE]:    "status_rookie",
};

// ── Status i18n key map ───────────────────────────────────────────────────────
export const USER_STATUS_I18N_KEY: Record<string, string> = {
  [UserStatusEnum.ACTIVE]:           "status_active",
  [UserStatusEnum.SUSPENDED]:        "status_suspended",
  [UserStatusEnum.BANNED]:           "status_banned",
  [UserStatusEnum.FROZEN_BY_USER]:   "status_frozen_by_user",
  [UserStatusEnum.DELETED_BY_USER]:  "status_deleted_by_user",
  [UserStatusEnum.DELETED_BY_ADMIN]: "status_deleted_by_admin",
  [UserStatusEnum.INACTIVE]:         "status_inactive",
};

// ── Helper functions ──────────────────────────────────────────────────────────

/** Returns CSS badge class for a role string. Falls back to "status-rookie". */
export function getRoleCssClass(role: string): string {
  return USER_ROLE_CSS[role.toUpperCase()] ?? "status-rookie";
}

/** Returns CSS badge class for a lifecycle status string. Falls back to "status-author". */
export function getStatusCssClass(status: string): string {
  return USER_STATUS_CSS[status.toUpperCase()] ?? "status-author";
}

/** Returns i18n key for a role string. Falls back to "status_rookie". */
export function getRoleI18nKey(role: string): string {
  return USER_ROLE_I18N_KEY[role.toUpperCase()] ?? "status_rookie";
}

/** Returns i18n key for a lifecycle status string. Falls back to "status_active". */
export function getStatusI18nKey(status: string): string {
  return USER_STATUS_I18N_KEY[status.toUpperCase()] ?? "status_active";
}

// ── Nickaltı owner marker ─────────────────────────────────────────────────────
//
// A nickaltı topic and the entries inside it survive everything that can happen
// to their owner: suspension, freeze, ban, admin removal and self-deletion all
// leave the topic standing. Without a marker on the title, a topic belonging to
// a banned or closed account looks exactly like a live author's, both in the
// left-pane list and on the topic page.
//
// This is the single source of truth for that marker so the list and the page
// can never disagree. It returns keys rather than JSX: the icon component is
// looked up in userStatusIconMap.ts by whoever renders, and the label comes
// from the same status i18n keys the profile and admin panel already use, so no
// third wording of the same fact can drift in.

export interface OwnerStatusMark {
  /** Key into USER_STATUS_ICON_MAP. */
  iconKey: string;
  /** CSS modifier suffix, e.g. "banned" → .topic-row__owner-mark--banned. */
  modifier: string;
  /** i18n key for the tooltip text. */
  tooltipKey: string;
}

/**
 * Marker for a nickaltı owner's lifecycle status, or null when there is nothing
 * to say (an active account, or a normal non-nickaltı topic whose ownerStatus
 * is undefined).
 *
 * @param status   UserStatus name from the API (case-insensitive)
 * @param isClosed whether users.closed_at is set — the only thing that separates
 *                 a self-deletion still inside its grace period from one that
 *                 has actually been carried out, since both keep the
 *                 DELETED_BY_USER status
 */
export function getNickaltiOwnerMark(
  status: string | null | undefined,
  isClosed?: boolean
): OwnerStatusMark | null {
  if (!status) return null;

  switch (status.toUpperCase()) {
    case UserStatusEnum.SUSPENDED:
      return { iconKey: "suspended", modifier: "suspended", tooltipKey: "status_suspended" };

    case UserStatusEnum.BANNED:
      return { iconKey: "banned", modifier: "banned", tooltipKey: "status_banned" };

    case UserStatusEnum.FROZEN_BY_USER:
      return { iconKey: "frozen", modifier: "frozen", tooltipKey: "status_frozen_by_user" };

    case UserStatusEnum.DELETED_BY_USER:
      // Same status either side of the grace period — closed_at is the divider.
      return isClosed
        ? { iconKey: "closed", modifier: "closed", tooltipKey: "status_closed_by_user" }
        : { iconKey: "deleting", modifier: "deleting", tooltipKey: "status_deleted_by_user" };

    case UserStatusEnum.DELETED_BY_ADMIN:
      return { iconKey: "removed", modifier: "removed", tooltipKey: "status_deleted_by_admin" };

    case UserStatusEnum.INACTIVE:
      return { iconKey: "inactive", modifier: "inactive", tooltipKey: "status_inactive" };

    // ACTIVE and anything unrecognised get no marker: an unknown status is
    // better shown as nothing than as a misleading icon.
    default:
      return null;
  }
}

// ── Suspension countdown ──────────────────────────────────────────────────────
//
// A suspension is a fixed-term penalty: the backend stores suspensionEndDate and
// both AuthService (on login) and SuspensionExpiryScheduler (hourly) restore the
// account to ACTIVE once that date passes. These helpers turn the raw date into
// something a tooltip can show, and are shared by ProfilePage and AdminUsers so
// the two never word the same fact differently.

export interface SuspensionRemaining {
  /** End date already passed — the account is waiting to be flipped back to ACTIVE. */
  expired: boolean;
  /** Whole days left, rounded up. 0 when less than an hour or already expired. */
  days: number;
  /** Whole hours left, rounded up. Only meaningful when days <= 1. */
  hours: number;
  /** Parsed end date, for formatting. */
  endDate: Date;
}

/**
 * Break a suspension end date down into what is left of it.
 *
 * Returns null when there is nothing to count down: no date at all (an
 * open-ended suspension that only an admin can lift), or an unparseable value.
 * Callers should fall back to the plain status label in that case.
 */
export function getSuspensionRemaining(
  suspensionEndDate: string | Date | null | undefined
): SuspensionRemaining | null {
  if (!suspensionEndDate) return null;

  const endDate = parseServerDate(suspensionEndDate);
  if (isNaN(endDate.getTime())) return null;

  const msLeft = endDate.getTime() - Date.now();
  if (msLeft <= 0) {
    return { expired: true, days: 0, hours: 0, endDate };
  }

  const hours = Math.ceil(msLeft / (1000 * 60 * 60));
  const days = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
  return { expired: false, days, hours, endDate };
}

/**
 * Build the human-readable "how much longer" fragment for a suspension.
 *
 * Under a day it counts in hours, because "1 gün kaldı" for something ending in
 * twenty minutes reads as wrong. Past the end date it says the term is over
 * rather than showing a countdown, since the account is already due to be
 * restored on the user's next login or the next scheduler run.
 *
 * @param t i18n translate function — typed as TFunction to match parseApiError.ts,
 *          the existing util that also translates with interpolation options
 */
export function getSuspensionRemainingText(
  suspensionEndDate: string | Date | null | undefined,
  t: TFunction
): string | null {
  const remaining = getSuspensionRemaining(suspensionEndDate);
  if (!remaining) return null;

  if (remaining.expired) return t("suspension_expired");
  if (remaining.days <= 1) return t("suspension_remaining_hours", { hours: remaining.hours });
  return t("suspension_remaining_days", { days: remaining.days });
}

/**
 * Full tooltip text for a suspended account: the status label, how much is left,
 * and the exact end date.
 *
 * Falls back to the plain status label for every other status, and for a
 * suspension with no end date — promising a countdown that will not happen is
 * worse than saying nothing.
 *
 * @param closedAt when set, a DELETED_BY_USER account is reported as closed
 *                 rather than as a deletion still in progress
 */
export function getStatusTooltip(
  status: string,
  suspensionEndDate: string | Date | null | undefined,
  t: TFunction,
  closedAt?: string | Date | null
): string {
  // A finished self-deletion keeps the DELETED_BY_USER status forever — the row
  // is closed, not removed — so the plain label would go on saying "hesabını
  // siliyor" long after the account was actually shut. closedAt is what marks
  // the request as carried out, exactly as the admin user table already does.
  if (closedAt && status.toUpperCase() === UserStatusEnum.DELETED_BY_USER) {
    return t("status_closed_by_user");
  }

  const label = t(getStatusI18nKey(status));
  if (status.toUpperCase() !== UserStatusEnum.SUSPENDED) return label;

  const remainingText = getSuspensionRemainingText(suspensionEndDate, t);
  if (!remainingText) return label;

  const endDate = parseServerDate(suspensionEndDate as string | Date);
  return `${label} — ${remainingText} (${formatDateTime(endDate)})`;
}